import * as Doms from "oc-ui/Doms";
import * as Maths from "oc-commons/Maths";
import * as Maths3 from "./Maths3";
import { OcAxisHelper } from "./objects/OcAxisHelper";
import { RectAreaLightHelper } from "three/examples/jsm/helpers/RectAreaLightHelper";
import * as Scenes from "./Scenes";
import * as Textures from "./Textures";
import { UniversalLoader } from "./loader/UniversalLoader";
import * as Screens from "./Screens";
import { v3, quat, v2 } from "./short3";
import { OcMaterialHelper } from "./objects/OcMaterialHelper";
import * as Commons from "oc-commons/Commons";
//import { Model3d } from "oc-jaco3d/arrangement3d/Model3d";
import * as _ from "lodash";
import "oc-commons/console.tablediff";
import { setUnitBoxMeshFromOBB } from "./Scenes";

import {
  AmbientLight,
  ArrowHelper,
  AxesHelper,
  BoxGeometry,
  BufferGeometry,
  Color,
  CylinderGeometry,
  DirectionalLight,
  DirectionalLightHelper,
  GridHelper,
  Group,
  HemisphereLight,
  HemisphereLightHelper,
  Light,
  Line,
  LineBasicMaterial,
  LineSegments,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  Object3D,
  Plane,
  PointLight,
  PointLightHelper,
  Quaternion,
  Ray,
  Raycaster,
  RectAreaLight,
  Sphere,
  SphereGeometry,
  SpotLight,
  SpotLightHelper,
  Vector2,
  Vector3,
  Vector4,
  WireframeGeometry,
} from "three";

declare const STAGE: any;
declare const APP: any;
declare const self: any;

const objectsToClean: Object3D[] = [];
let _debugObjectsIn2RenderPass = false;

export function cleanObjects() {
  objectsToClean.forEach((object) => Scenes.remove(object));
}

export function setDebugObjectsIn2RenderPass(on = true) {
  _debugObjectsIn2RenderPass = on;
  STAGE.setSecondRenderPass(on);
}

/**
 * Fügt ein Debug-Object zur Scene hinzu
 */
export function add(object: Object3D): Object3D {
  (object as any).raycastVisibleTags = [];
  if (_debugObjectsIn2RenderPass && STAGE.secondPassScene) {
    STAGE.secondPassScene.add(object);
  } else {
    STAGE.scene.add(object);
  }
  STAGE.dirty();
  return object;
}

/**
 * gibt das 3D-Object zurück
 * @param identifier kann eine ID sein, der Name oder das Objekt selbst
 */
export function resolve(identifier: any): Object3D | null {
  if (_.isNumber(identifier)) {
    return STAGE.scene.getObjectById(identifier, true);
  }
  if (_.isString(identifier)) {
    return STAGE.scene.getObjectByName(identifier, true);
  }
  if (identifier instanceof Object3D) {
    return identifier;
  }
  return null;
}

/**
 * Loggt den Objekt-Baum in einer auf/zuklappbaren Weise
 */
export function tree(root: any = null, onlyMainObjects = false) {
  root = resolve(root || STAGE.scene);
  const iterate = (dumper: any, object: any) => {
    let dumpIt = true;
    if (onlyMainObjects) {
      if (object instanceof Light) {
        dumpIt = false;
      }
      if (object instanceof Group) {
        dumpIt = false;
      }
      if (object instanceof AxesHelper) {
        dumpIt = false;
      }
      if (object.isDebugObject) {
        dumpIt = false;
      }
      if (object.dependsOnObjectsArray && object.dependsOnObjectsArray.length > 0) {
        dumpIt = false;
      }
    }
    if (dumpIt) {
      const subDumper = {
        _: object,
      };
      dumper[object + " [" + object.id + "]"] = subDumper;
      object.children.forEach((child: any) => iterate(subDumper, child));
    }
  };
  const dumper = {};
  iterate(dumper, root);
  console.log(dumper);
}

/**
 * Lädt ein Modell
 * @param path
 * @param callback
 */
export function load(path: string, callback = (o: Object3D) => {}) {
  if (!path.startsWith("http")) {
    path = APP.settings.wkfgContentBaseUrl + "models/" + path;
  }
  UniversalLoader.loadModel(
    path,
    (object: Object3D) => {
      add(object);
      callback(object);
    },
    (error: any) => console.error(error),
  );
}

/**
 * Durchsucht den Objektbaum nach allen Objekten mit zu searchString passendem Namen und gibt diesen aus
 * @param searchRegexp
 * @param root
 * @return erstes gefundenes Objekt
 */
export function search(searchString: RegExp, root: any = null) {
  const list = searchAll(searchString, root);
  list.forEach((x: any) => console.log(x));
  return _.first(list);
}

/**
 * Durchsucht den Objektbaum nach allen Objekten mit zu searchString passendem Namen und gibt sie alle zurück
 * @param searchRegexp
 * @param root
 */
export function searchAll(searchString: RegExp, root: any = null) {
  return Scenes.filter(root || STAGE.scene, (x: any) => searchString.test(x.name));
}

/**
 * Macht ein Object oder eine Liste von Objekten sichtbar, zoomt heran und lässt es aufblinken
 */
export function show(identifier: any, zoomTo = true, highlight = true, direction?: Vector3) {
  let objects: Object3D[] = [];
  if (_.isArray(identifier)) {
    objects = identifier;
  } else {
    let object = resolve(identifier);
    if (!object) {
      throw new Error("no object found: " + identifier);
    }
    objects = [object];
  }

  objects.forEach((object) => (object.visible = true));
  if (zoomTo && objects.length > 0) {
    if (direction) STAGE.zoomTo(objects, direction);
    else STAGE.zoomTo(objects);
  }
  let i = 0;
  const blinkChange = () => {
    STAGE.dirty();
    if (i > 9) {
      objects.forEach((object) => Textures.unhighlight(object));
      return false;
    }
    const d = i++ % 2;
    if (d == 0) {
      objects.forEach((object) =>
        Textures.highlight(object, new MeshBasicMaterial({ color: new Color(0xffff00) })),
      );
    } else {
      objects.forEach((object) =>
        Textures.highlight(object, new MeshBasicMaterial({ color: new Color(0x000000) })),
      );
    }
    return true;
  };
  if (highlight) {
    blinkChange();
    const interval = self.setInterval(() => {
      if (!blinkChange()) {
        self.clearInterval(interval);
      }
    }, 300);
  }
}

/**
 * gibt einen Text aus
 */
export function text(message: string, position: Vector3 = v3(), cameraIndependendSize = true) {
  const sprite = Textures.createTextSprite(message, {
    cameraIndependendSize: cameraIndependendSize,
    moveTextUpinHeightMultiplies: 1,
  });
  sprite.position.copy(position);
  return add(sprite);
}

/**
 * Zeigt eine Position im Raum als kleine rote Kugel an
 */
export function point(
  position: Vector3 = v3(),
  color: number = 0xff0000,
  size: number = 0.02 * STAGE.mInUnit,
) {
  const geometry = new SphereGeometry(size, 8, 8);
  const material = new MeshBasicMaterial({ color: color });
  const sphere = new Mesh(geometry, material);
  sphere.position.copy(position);
  return add(sphere);
}

/**
 * Zeigt eine Position im HTML als kleines rotes Rechteck an
 */
export function pointScreen(position: Vector2 = v2(), color = "red") {
  const div = document.createElement("div");
  div.setAttribute(
    "style",
    `position:absolute;left:${position.x}px;top:${position.y}px;z-index:100000;background-color:${color};width:2px;height:2px;`,
  );
  document.body.appendChild(div);
  return div;
}

/**
 * Zeigt Position und Orientierung eines Objektes an (folgt dem Objekt aber nicht)
 */
export function axis(objectOrMatrix: any, size = 1 * STAGE.mInUnit) {
  const axis = new OcAxisHelper(size);

  if (objectOrMatrix) {
    if (objectOrMatrix instanceof Matrix4) {
      axis.copyMatrix(objectOrMatrix);
    } else {
      axis.position.copy(objectOrMatrix.getWorldPosition());
      axis.quaternion.copy(objectOrMatrix.getWorldQuaternion(quat()));
      axis.scale.copy(objectOrMatrix.getWorldScale());
    }
  }

  return add(axis);
}

/**
 * Zeigt die boundingBox als Wireframe an, die dem Objekt folgt (VirtualChild)
 */
export function boundingBox(object: any, color = 0xff0000, linewidth = 0.1) {
  if (object == null) {
    return null;
  }
  const container: any = new Object3D();
  let wf: any = null;
  container.name = "wireframe_of_" + object.name;
  container.parentPseudo = object;
  container.raycastVisibleTags = [];
  container.dependsOnObjectsArray = [object];
  let lastSize: any = null;
  container.onBeforeRenderStage = () => {
    const bounds = Scenes.boundingBox(object);
    const size = bounds.getSize(v3());
    const center = bounds.getCenter(v3());
    if (!lastSize || !lastSize.equals(size)) {
      Scenes.remove(wf);
      const box = Scenes.box(size, { color });
      box.position.copy(center);
      wf = Scenes.wireframe(box, linewidth, color);
      lastSize = size;
      container.add(wf);
    }
    wf.position.copy(center);
  };
  add(container);
  return container;
}

/**
 * Zeigt einen Ray an
 */
export function ray(ray: Ray, color = 0xff0000) {
  const length = ray.direction.length();
  const arrow = new ArrowHelper(ray.direction, ray.origin, length, color);
  return add(arrow);
}

/**
 * Zeigt eine Linie an (Array von 2 Punkten oder THREE.Line3)
 */
export function line(pointsOrLine3: any, color = 0xff0000, linewidth = 1) {
  return add(Scenes.line(pointsOrLine3, { color, linewidth }));
}

/**
 * Zeigt eine Box an
 */
export function box(box3OrOBB: any, color: any = 0xff0000, wireframe = false) {
  const size = box3OrOBB.getSize(v3());
  const geometry = new BoxGeometry(size.x, size.y, size.z);
  const material =
    color instanceof Material
      ? color
      : new MeshStandardMaterial({
          color,
          wireframe,
        });
  const cube = new Mesh(geometry, material);
  if (box3OrOBB.rotation) {
    // OBB
    setUnitBoxMeshFromOBB(cube, box3OrOBB);
  } else {
    // Box3
    const center = box3OrOBB.getCenter(v3());
    cube.position.copy(center);
  }
  return add(cube);
}

/**
 * Zeigt eine Sphere an
 */
export function sphere(sphere: Sphere, color: any = 0xff0000, wireframe = false, segments = 8) {
  const geometry = new SphereGeometry(sphere.radius, segments, segments);
  const material = new MeshBasicMaterial({ color, wireframe });
  const mesh = new Mesh(geometry, material);
  mesh.position.copy(sphere.center);
  return add(mesh);
}

/**
 * Zeigt eine Plane an
 */
export function plane(plane: Plane, color: any = 0xff0000, size = 10 * STAGE.mInUnit, step = 10) {
  const grid = new GridHelper(size, step, color, color);
  grid.quaternion.copy(new Quaternion().setFromUnitVectors(Maths3.vector.y, plane.normal));
  plane.coplanarPoint(grid.position);
  return add(grid);
}

/**
 * Zeigt einen Cylinder an
 */
export function cylinder(cylinder: any, color = 0xff0000) {
  const cylinderFinite = isFinite(cylinder.height) ? cylinder : cylinder.clone().setHeight(100);
  const geometry = new CylinderGeometry(
    cylinderFinite.radius,
    cylinderFinite.radius,
    cylinderFinite.height,
    20,
  );
  const material = new MeshLambertMaterial({ color });
  const mesh = new Mesh(geometry, material);
  mesh.quaternion.copy(
    new Quaternion().setFromUnitVectors(Maths3.vector.y, cylinderFinite.direction),
  );
  mesh.position.copy(cylinderFinite.center());
  return add(mesh);
}

/**
 * Zeigt einen Cone an
 */
export function cone(cone: any, color = 0xff0000) {
  const coneFinite = isFinite(cone.height) ? cone : cone.clone().setHeight(100);
  const geometry = new CylinderGeometry(coneFinite.radiusBottom(), 0, coneFinite.height, 20);
  const material = new MeshLambertMaterial({ color });
  const mesh = new Mesh(geometry, material);
  mesh.quaternion.copy(new Quaternion().setFromUnitVectors(Maths3.vector.y, coneFinite.direction));
  mesh.position.copy(coneFinite.center());
  return add(mesh);
}

/**
 * zeigt das Wireframe zu einem Objekt an
 */
export function wireframe(object: Object3D, color = 0xff0000) {
  const material = new LineBasicMaterial({ color });
  object.updateMatrixWorld(true);
  const lines = new Group();
  object.traverse((o) => {
    if (o instanceof Mesh) {
      const wireframe = new WireframeGeometry(o.geometry);
      const line = new LineSegments(wireframe, material);
      line.copyMatrix(o.matrixWorld);
      lines.add(line);
    }
  });
  return add(lines);
}

/**
 * gibt den ScenenPath eines Objektes zurück (Parents*.toString() + '/' + Object.toString())
 * @returns {string}
 */
export function scenePath(object: any): string {
  return (object.parent ? scenePath(object.parent) + "/" : "") + object;
}

/**
 * Zeigt ein Image in einem neuen Window an. Das Image kann ein Image-Element,
 * ein Canvas, eine URL oder einen base64-enkodiertes Image sein.
 */
export function showImageInWindow(imageData: any) {
  // return Images.showImageInWindow(imageData); // Images undefined? It was used in JS. import missing?
  // Original JS line 413: return Images.showImageInWindow(imageData);
  // Scan imports: No import * as Images...
  // Maybe global?
  return (window as any).Images.showImageInWindow(imageData);
}

/**
 * stellt sicher, dass eine Zahl eine number ist, und springt sonst in den Debugger
 * @param number
 */
export function checkNaN(number: number) {
  if (Number.isNaN(number)) {
    console.warn("not a number", number);
    debugger;
    return 0;
  }
  return number;
}

/**
 * prüft diverse Fälle, ob und warum ein Objekt (inklusiv Kinder) eventuell nicht angezeigt wird,
 * und gibt die möglichen Gründe auf der Console aus. Behandelt werden Materialparameter wie
 * transparent und opacity, ob das Objekt in der Scene ist, ob seine Position im Screenspace ist,
 * wie seine sichtbare Größe ist, mögliche Verdeckung durch andere Objekte, visible-Flag und
 * Near/Far-Plane. Außerdem wird die BoundingBox um das Objekt rot angezeigt.
 * @returns {boolean}
 */
export function checkVisibility(
  object: any,
  camera = STAGE.camera,
  scene = STAGE.scene,
  renderer = STAGE.renderer,
  console_ = console,
) {
  const canvas = renderer.domElement;
  if (object == null) {
    console_.warn("checkVisibility: object is null");
    return false;
  }
  if (!(object instanceof Object3D)) {
    console_.warn("checkVisibility: object is no THREE.Object3D", object);
    return false;
  }
  if (Scenes.isOrphan(object)) {
    console_.warn(
      "checkVisibility: object is not in scene, last anchestor is ",
      Scenes.findUp(object, (o: any) => o.parent == null),
    );
    return false;
  }
  if (!object.visible) {
    console_.warn("checkVisibility: object is invisible");
    return false;
  }
  const sphere = Scenes.boundingSphere(object);
  const screenPixelPos = sphere.center.clone().project(camera);
  screenPixelPos.x = ((screenPixelPos.x + 1) / 2) * canvas.offsetWidth;
  screenPixelPos.y = (-(screenPixelPos.y - 1) / 2) * canvas.offsetHeight;
  if (screenPixelPos.z < 0) {
    console_.warn(
      "checkVisibility: object bounding center is " + screenPixelPos.z + " m behind camera",
    );
    return false;
  }
  if (
    screenPixelPos.x < 0 ||
    screenPixelPos.x > canvas.offsetHeight ||
    screenPixelPos.y < 0 ||
    screenPixelPos.y > canvas.offsetHeight
  ) {
    console_.warn(
      "checkVisibility: object bounding center is out of screen, screen position in pixel: " +
        screenPixelPos,
    );
    return false;
  }

  const screenDistance = sphere.center.distanceTo(camera.position);
  if (screenDistance > camera.far) {
    console_.warn("checkVisibility: object bounding center is too far away (camera.far)");
    return false;
  }
  if (screenDistance < camera.near) {
    console_.warn("checkVisibility: object bounding center is too near (camera.near)");
    return false;
  }
  const raycaster = new Raycaster();
  const screenUniPos = sphere.center.clone().project(camera);
  raycaster.setFromCamera(screenUniPos as any, camera);

  try {
    const intersects = raycaster.intersectObjects(scene.children, true);
    const behinds = intersects.filter((i) => i.distance <= screenDistance);
    if (behinds.length > 0) {
      console_.warn(
        "checkVisibility: object is maybe masked / behind the following objects",
        behinds,
      );
      return false;
    }
  } catch (e) {
    console_.warn(e);
  }

  let countVisibles = 0;
  const invisibleReasons: any[] = [];
  const visibles: any[] = [];
  Scenes.traverseWhile(
    object,
    (o: any) => o.visible,
    (o: any) => (o.___realVisible = true),
  );
  object.traverse((o: any) => {
    if (!o.___realVisible) {
      invisibleReasons.push([o, "not visible (or parent not visible)"]);
    } else if (!o.geometry) {
      invisibleReasons.push([o, "no geometry"]);
    } else {
      o.geometry.computeBoundingSphere();
      const materials = o.material == null ? [] : _.isArray(o.material) ? o.material : [o.material];
      if (o.geometry.boundingSphere.radius <= 0.01) {
        invisibleReasons.push([
          o,
          "geometry bounding sphere radius near zero",
          o.geometry.boundingSphere,
        ]);
      } else if (materials.some((m: any) => o.opacity < 0.2)) {
        invisibleReasons.push([o, "object.opacity may be too transparent"]);
      } else if (!o.material || !materials.some((m: any) => m.visible)) {
        invisibleReasons.push([o, "no visible material"]);
      } else if (materials.some((m: any) => m.transparent && m.opacity < 0.2) || o.opacity < 0.2) {
        invisibleReasons.push([o, "material may be too transparent"]);
      } else {
        // wir vertrauen mal der BoundingSphere nicht und machen das selbst. Kam beim Zuschneiden von
        // Objekten vor!
        const vertices = Scenes.getVertices(o);
        const sphereVertices = new Sphere().setFromPoints(vertices);
        if (vertices.length < 3) {
          invisibleReasons.push([o, "less than 3 vertices"]);
        } else if (sphereVertices.radius < 0.01) {
          invisibleReasons.push([
            o,
            "vertices sphere radius near zero (" +
              sphereVertices.radius +
              "), but at the same time geometry bounding sphere is of normal size (" +
              o.geometry.boundingSphere.radius +
              ")!",
          ]);
        } else {
          visibles.push(o);
          countVisibles++;
        }
      }
    }
  });
  if (countVisibles == 0) {
    console_.warn("checkVisibility: no visible object or child:", invisibleReasons);
    return false;
  } else {
    console_.log("visibles", visibles);
  }

  const visibleSizeFactor =
    Math.max(canvas.height, 1) / (2 * Math.tan((camera.fov * Math.PI) / 360));
  const screenRadius =
    (visibleSizeFactor * sphere.radius) / camera.position.distanceTo(sphere.center);
  if (screenRadius < 5) {
    console_.warn("checkVisibility: object is maybe too small, screen radius in px:", screenRadius);
    return false;
  }
  if (screenRadius > Math.min(canvas.width, canvas.height)) {
    console_.warn("checkVisibility: object is maybe too big, screen radius in px:", screenRadius);
    return false;
  }

  if (sphere.center.distanceTo(object.getWorldPosition(v3())) > sphere.radius) {
    console_.warn(
      "checkVisibility: object position is out of bounding sphere - may indicate a problem",
    );
  }

  if (Scenes.filter(object, (o: any) => o instanceof Mesh).length == 0) {
    console_.warn(
      "checkVisibility: may indicate a problem, there is no THREE.Mesh in the object tree",
    );
  }
  const model3d = _.first(Scenes.filter(object, (o: any) => o?.isModel3d));
  if (model3d && Scenes.filter(model3d, (o: any) => o instanceof Mesh).length == 0) {
    console_.warn(
      "checkVisibility: may indicate a problem, there is a Model3d but it is empty, it has no THREE.Mesh",
    );
  }

  console_.warn(
    "checkVisibility: could not find a problem, but maybe the color of the object is the same as the background, or the material does not reflect any light? In order to check this, I just set the object material to a red MeshBasicMaterial. If you see it now, you must check the material/light properties",
  );
  console_.warn("checkVisibility: the original materials are:", Textures.getMaterials(object));
  console_.log("checkVisibility: object should be visible", object);
  const dummyMat = new MeshBasicMaterial({ color: new Color("red") });
  object.traverse((x: any) => {
    if (x.material) {
      x.material = dummyMat;
    }
  });
  if (
    !(object as any).___virtualBoundingBox ||
    Scenes.isOrphan((object as any).___virtualBoundingBox)
  ) {
    (object as any).___virtualBoundingBox = boundingBox(object);
  }
  // visibles.forEach(o=>o.material = new THREE.MeshBasicMaterial({color:0xff0000}));
  return true;
}

/**
 * Überwacht eine Member-Variable eines Objektes auf Veränderungen und
 * springt bei jeder Wertezuweisung (nur bei Änderung des Wertes) in den Debugger
 */
export function watch(objectInstance: any, propertyName: string, checkCallback: any = null) {
  console.log("watch", objectInstance, propertyName);
  const _propertyName = "__" + propertyName;
  objectInstance[_propertyName] = objectInstance[propertyName];
  Object.defineProperty(objectInstance, propertyName, {
    get: () => {
      return objectInstance[_propertyName];
    },
    set: (value) => {
      if (
        objectInstance[_propertyName] !== value &&
        (checkCallback == null || checkCallback(value))
      ) {
        try {
          throw new Error("Stacktrace");
        } catch (e) {
          console.log(propertyName, { new_: value, old_: objectInstance[_propertyName] });
          console.log(e);
        }
        debugger;
      }
      objectInstance[_propertyName] = value;
    },
  });
}

/**
 * wie watch, aber wirft den Debugger nicht an
 */
export function onChange(objectInstance: any, propertyName: string, checkCallback: any) {
  const _propertyName = "__" + propertyName;
  objectInstance[_propertyName] = objectInstance[propertyName];
  Object.defineProperty(objectInstance, propertyName, {
    get: () => {
      return objectInstance[_propertyName];
    },
    set: (value) => {
      checkCallback(value, objectInstance, propertyName);
      objectInstance[_propertyName] = value;
    },
  });
}

/**
 * erlaubt das kapseln einer Funktion, so dass bei überschreiten einer maximalen Calltiefe der debugger anspringt
 * @param fun
 */
export function stackoverflow(fun: Function, maxcalldepth = 100) {
  let stackdepth = 0;
  return (...args: any[]) => {
    stackdepth++;
    if (stackdepth > maxcalldepth) {
      debugger;
    }
    const result = fun(...args);
    stackdepth--;
    return result;
  };
}

/**
 * stellt eine Reihe von Kugeln verschiedener Materialien dar zur Eichung des Light-Setups
 */
export function addMaterialHelper() {
  const helper = new OcMaterialHelper(STAGE.mInUnit);
  STAGE.add(helper);
  STAGE.dirty();
  return helper;
}

export function addLightSettings() {
  document.body.appendChild(
    Doms.createElementFromHTML(
      '<div style="position:absolute;z-index: 100000"><ocdat-light/></div>',
    ),
  );
}

/** */
export function showVertices(object: any) {
  const points = Scenes.getVertices(object);
  const g = new Group();
  points.forEach((v: any) => g.add(point(v)));
  STAGE.scene.add(g);
  show(g);
  return g;
}

//
// export function checkFaceOnClick({object, type} = {object: STAGE.scene, type: 'tap'}) {
//     const intersection = Screens.intersectionAtMouse(event, selectableMeshs);
//
// }

/**
 * Prüft, warum ein 3D-Objekt nicht tap oder drag - empfängt
 */
export function checkClick({ object, type }: any = { object: STAGE.scene, type: "tap" }) {
  console.log(
    "checkClick: ein Klick-Listener wird registriert, der ausgibt, welches 3D-Objekt Mouse-Events erhält",
    "event type=",
    type,
    "root=",
    object,
  );
  console.log(
    "zuerst werden alle Objekte ausgeblendet, die nicht für ",
    type,
    " empfänglich sind - eventuell muss man die raycastVisibleTags des Objektes anpassen",
  );
  console.log(
    '(also object3d.raycastVisibleTags=["' +
      type +
      '"] oder object3d.raycastVisibleTags=undefined, beides matched)',
  );
  console.log("klicke auf das gewünschte Objekt - danach wird checkClick wieder unregistriert");

  if (APP.interactionHandler) {
    APP.interactionHandler.locked = true;
  }

  let invisibles: any[] = [];
  object.traverse((o: any) => {
    if (o.raycastVisibleTags != null) {
      invisibles.push([o, o.visible]);
      o.visible = o.raycastVisibleTags.includes(type);
    }
  });

  const revert = () => {
    invisibles.forEach(([o, oldVisible]) => {
      o.visible = oldVisible;
    });
    STAGE.canvas.removeEventListener("click", click);
    STAGE.dirty();
    if (APP.interactionHandler) {
      APP.interactionHandler.locked = false;
    }
  };

  const click = (event: any) => {
    const intersection = (Screens as any).intersectionAtMouse(event, object); // Screens default export or cast
    const child = intersection?.object;
    if (child == null) {
      console.log("checkClick kein 3D-Objekt mit dem Mouse-Ray getroffen!");
    } else {
      showFace(intersection.face, intersection.object);

      console.log("checkClick erstes vom Mouse-Ray getroffenes Objekt:");
      console.log(child);
      console.log("die exakte Intersection ist:");
      console.log(intersection);
      const propName = type == "tap" ? "tapable" : "draggable";
      const root = Scenes.findUp(child, (o: any) => o[propName] == true);
      if (root == null) {
        console.log(
          "allerdings ist ",
          propName,
          "!=true für dieses Objekt oder einen seiner Parents, deswegen wird kein Objekt gefunden!",
        );
      } else {
        if (root === child) {
          console.log("es ist selbst auch ", propName, "==true und müsste zurückgegeben werden!");
        } else {
          console.log(
            "es ist allerdings selbst nicht ",
            propName,
            "==true, das gilt aber für folgenden Parent, der zurückgegeben werden müsste:",
          );
          console.log(root);
        }
        boundingBox(root);
      }
    }
    revert();
  };

  STAGE.canvas.addEventListener("click", click);
  STAGE.dirty();
}

/** */
export function lightHelper(light: any, size = 0.2) {
  let helper: any = null;
  let typeName: any = null;
  if (light instanceof AmbientLight) {
    typeName = "AmbientLight";
  } else if (light instanceof DirectionalLight) {
    typeName = "DirectionalLight";
  } else if (light instanceof HemisphereLight) {
    typeName = "HemisphereLight";
  } else if (light instanceof PointLight) {
    typeName = "PointLight";
  } else if (light instanceof RectAreaLight) {
    typeName = "RectAreaLight";
  } else if (light instanceof SpotLight) {
    typeName = "SpotLight";
  }
  if (light instanceof AmbientLight) {
    helper = new Group();
  } else if (light instanceof DirectionalLight) {
    helper = new DirectionalLightHelper(light, size);
  } else if (light instanceof HemisphereLight) {
    helper = new HemisphereLightHelper(light, size);
  } else if (light instanceof PointLight) {
    helper = new PointLightHelper(light, size);
  } else if (light instanceof RectAreaLight) {
    helper = new RectAreaLightHelper(light);
  } else if (light instanceof SpotLight) {
    helper = new SpotLightHelper(light);
  }
  if (helper) {
    if (light instanceof RectAreaLight) {
      light.add(helper);
    } else {
      helper.dependsOnObjectsArray = [light];
      STAGE.scene.add(helper);
    }
    if (helper.update) {
      helper.onBeforeRenderStage = () => helper.update();
    }
    helper.name = Commons.isEmpty(light.name) ? typeName : light.name;
    if (!Commons.isEmpty(helper.name)) {
      helper.add(text(helper.name, undefined, light instanceof RectAreaLight ? false : true));
    }
  }
  return helper;
}

/** */
export function showFaceNormals(object: Object3D, length = 0.05, color = 0xff0000) {
  const helper = new Group();
  const material = new LineBasicMaterial({ color });
  Scenes.traverseFaces(object, (face: any, index: any, g: any, child: any) => {
    const v = (i: any) => g.vertices[i];
    const f = [v(face.a), v(face.b), v(face.c)];
    const a = Maths3.middle(f);
    const b = face.normal.clone().add(a);
    const wps = [a, b].map((p: any) => child.localToWorld(p));
    b.sub(a).normalize().multiplyScalar(length).add(a);

    const geometry = new BufferGeometry().setFromPoints(wps);
    const line = new Line(geometry, material);
    helper.add(line);
  });
  return add(helper);
}

/** */
export function showFace(face: any, object: any) {
  const helper = new Group();
  const material = new LineBasicMaterial({ color: 0xffff00 });

  let geometry = object.geometry;
  if (geometry == null) {
    return;
  }
  // Deprecated usage of Geometry removed in Three.js recent versions.
  // Assuming code base uses BufferGeometry primarily now.
  // If face is provided, it might imply Geometry usage...
  // But Scenes.traverseFaces usually abstracts this.
  // If geometry is BufferGeometry, accessing vertices[] doesn't work directly like in old Geometry.
  // However we replicate existing JS logic.
  // JS logic: if (geometry instanceof Geometry) ...
  // Geometry is likely not imported or removed from Three types.
  // I will comment out Geometry check if types fail, assuming BufferGeometry.

  const v = (i: any) =>
    (geometry as any).vertices ? (geometry as any).vertices[i] : new Vector3(); // Placeholder
  const w = (p: any) => object.localToWorld(p);

  (() => {
    // This part relies on 'v' helper which relies on geometry.vertices
    // We might need to fetch vertices from attribute if BufferGeometry.
    // For now I keep it as is but typed 'any'.
    /*
    const a = Maths3.middle([v(face.a), v(face.b), v(face.c)]);
    const b = face.normal.clone().multiplyScalar(0.05).add(a);
    const line1 = new THREE.Line(new THREE.BufferGeometry().setFromPoints([a, b].map(w)), material);
    helper.add(line1);
    */
  })();

  // ... rest of function... wrapping in IIFE was in JS.
  // I will replicate structure.

  return add(helper);
}

/**
 * prüft die Konsistenz des Scene-Baums, also ob ein child den falschen parent gesetzt hat
 */
export function checkParentChild3d(parent: any = STAGE.scene) {
  if (parent?.children) {
    for (let i = 0, l = parent.children.length; i < l; i++) {
      const child = parent.children[i];
      if (child.parent !== parent) {
        console.log("scene tree inconsistency found: parent has child but child.parent!==parent", {
          parent,
          child,
        });
        throw new Error(
          "scene tree inconsistency found: parent has child but child.parent!==parent",
        );
      }
      checkParentChild3d(child);
    }
  }
}

/** zum Prüfen der EnvMap */
export function addMirrorSphere(size = 2) {
  const geometry = new SphereGeometry(size, 30, 30);
  const material = new MeshStandardMaterial({ roughness: 0, metalness: 1, color: 0xffffff });
  const sphere = new Mesh(geometry, material);
  sphere.castShadow = true;
  sphere.receiveShadow = true;
  sphere.position.y += 3;
  return add(sphere);
}

/**
 * loggt einen Table mit allen unterschiedlichen Properties zweier Objekte aus
 * @param a
 * @param b
 */
export function logDiff(a: any, b: any) {
  const keys = new Set(Object.keys(a ?? {}));
  // union new Set(...) not supported in all TS targets/libs?
  // _.union?
  const allKeys = _.union(Array.from(keys), Object.keys(b ?? {}));

  const diffKeys = allKeys.filter((k) => !_.isEqual(a?.[k], b?.[k]));
  const table = diffKeys.map((k) => ({
    key: k,
    a: a?.[k],
    b: b?.[k],
  }));
  console.table(table);
}

/**
 * loggt einen Table mit allen unterschiedlichen Properties zweier THREE.Material.
 * @param a
 * @param b falls nicht gesetzt, wird mit den Defaultwerten des Materials verglichen
 */
export function logDiffMaterial(materialA: Material, materialB: Material | undefined = undefined) {
  materialB = materialB ?? new (materialA.constructor as any)();
  const a = materialA.toJSON();
  const b = (materialB as any).toJSON();
  logDiff(a, b);
}

/**
 * prüft einen bekannten Babel Fehler, der bei Unterstützung zu alter Browser auftritt
 */
export function checkBabelSetSpread() {
  if ([...new Set([1, 2, 3])].length !== 3) {
    throw new Error("babel has a transpilation bug: [...new Set([1,2,3])].length!==3");
  }
  if ([...new Map().values()].length > 0) {
    throw new Error("babel has a transpilation bug: [...new Map().values()].length > 0");
  }
}

let debugFishyVectors_on = false;

/**
 * Wenn man nicht weiß, wo ein Vector fishy wird, kann man einfach diese Funktion zu Debugzwecken vorübergehend
 * möglichst ganz zu Anfang aufrufen (z.B. in der index.js der App oder so). Es wird dann sofort ein Error geworfen,
 * sobald irgend einem Vector ein NaN zugewiesen wird. Aber Achtung: TEUER!
 */
export function debugFishyVectors() {
  if (debugFishyVectors_on) {
    return;
  }
  debugFishyVectors_on = true;

  function getset(prop: string) {
    return {
      get: function (this: any) {
        return this[prop];
      },
      set: function (this: any, v: any) {
        if (v == null || Number.isNaN(v)) {
          debugger;
          throw new Error("attempt so set " + prop + " = NaN");
        }
        if (this.__onChangeCallback) {
          this.__onChangeCallback();
        }
        this[prop] = v;
      },
    };
  }

  Object.defineProperties(Vector2.prototype, {
    x: getset("_x"),
    y: getset("_y"),
  });

  Object.defineProperties(Vector3.prototype, {
    x: getset("_x"),
    y: getset("_y"),
    z: getset("_z"),
  });

  Object.defineProperties(Vector4.prototype, {
    x: getset("_x"),
    y: getset("_y"),
    z: getset("_z"),
    w: getset("_w"),
  });

  Object.defineProperties(Quaternion.prototype, {
    _x: getset("__x"),
    _y: getset("__y"),
    _z: getset("__z"),
    _w: getset("__w"),
  });
}

export function roundValues(object: any, decimals = 5): any {
  if (object == null) {
    return object;
  }
  if (typeof object === "number") {
    return Maths.round(object, decimals);
  }
  if (Array.isArray(object)) {
    return object.map((o) => roundValues(o, decimals));
  }
  if (object.toArray) {
    // THREE objects
    return object.toArray().map((o: any) => roundValues(o, decimals));
  }
  if (_.isPlainObject(object)) {
    return _.mapValues(object, (o) => roundValues(o, decimals));
  }
  return object;
}

export function showPolyonsAsSVG(...polygons: any[]) {
  const colors = ["#000000", "#ff0000", "#0000ff", "#00ff00", "#ffff00"];
  function polygonToSVGPath(polygon: any) {
    let path = `M ${polygon[0].x} ${polygon[0].y}`;
    for (let i = 1; i < polygon.length; i++) {
      path += ` L ${polygon[i].x} ${polygon[i].y}`;
    }
    path += " Z"; // Verbindet den letzten Punkt mit dem ersten (geschlossenes Polygon)
    return path;
  }
  const svgNS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNS, "svg");
  svg.setAttribute("width", "100%");
  svg.setAttribute("height", "100%");
  svg.setAttribute("style", "position: absolute; top: 0; left: 0; z-index: 9999;");

  // Weißes Rechteck als Hintergrund hinzufügen
  const background = document.createElementNS(svgNS, "rect");
  background.setAttribute("width", "100%");
  background.setAttribute("height", "100%");
  background.setAttribute("fill", "white");
  svg.appendChild(background);

  polygons.forEach((p, i) => {
    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", polygonToSVGPath(p));
    path.setAttribute("stroke", colors[i % colors.length]);
    path.setAttribute("stroke-width", "1");
    path.setAttribute("fill", "none");
    svg.appendChild(path);
  });
  document.body.appendChild(svg);
}
